import { CommonModule } from '@angular/common';
import {
  Component,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  inject,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ChartWrapperService, ChartWrapperInstance } from '../../shared-services/chart-wrapper.service';
import { ConfigService } from '../../shared-services/config.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';

interface AxisDriverRow {
  label: string;
  status: string;
}

interface AxisDriverStatusStyle {
  background?: string;
  color?: string;
}

interface AxisDriversConfig {
  widgetId?: string;
  heading?: string;
  eyebrow?: string;
  subheading?: string;
  title?: string;
  apiUrl?: string;
  method?: string;
  data?: unknown;
  items?: unknown;
  rows?: unknown;
  dataKey?: string;
  dataPath?: string;
  responsePath?: string;
  labelKey?: string;
  statusKey?: string;
  height?: string;
  heightSm?: string;
  heightMd?: string;
  heightLg?: string;
  filterListener?: string | string[];
  filterListner?: string | string[];
  statusStyles?: Record<string, AxisDriverStatusStyle>;
}

@Component({
  selector: 'app-axis-drivers',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './axis-drivers.component.html',
  styleUrls: ['./axis-drivers.component.scss'],
})
export class AxisDriversComponent implements OnInit, OnChanges, OnDestroy {
  @Input() data?: AxisDriversConfig;

  rows: AxisDriverRow[] = [];
  loading = false;
  errorMessage = '';
  fontScale = 1;

  private readonly configService = inject(ConfigService);
  private readonly chartWrapperService = inject(ChartWrapperService);
  private readonly responsiveLayoutService = inject(ResponsiveLayoutService);
  private readonly wrapper: ChartWrapperInstance = this.chartWrapperService.createInstance();
  private dataSub?: Subscription;
  private reflowSub?: Subscription;
  private lastFilterLoadSignature = '';

  private readonly defaultStatusStyles: Record<string, AxisDriverStatusStyle> = {
    good: {
      background: '#e9f4df',
      color: '#0f3d12',
    },
    fair: {
      background: '#f8ead2',
      color: '#5f3500',
    },
    weak: {
      background: '#fae8e9',
      color: '#a31515',
    },
  };

  ngOnInit(): void {
    this.applyResponsiveSizing();
    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.applyResponsiveSizing();
    });
    const initialFilters = this.wrapper.attach(this.data, {
      onReload: (filters) => this.loadRows(filters),
      getChart: () => null,
      getSeriesNames: () => [],
      getCategories: () => [],
    });
    this.loadRows(initialFilters || {});
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange) {
      this.applyResponsiveSizing();
      this.wrapper.updateData(this.data);
      this.loadRows();
    }
  }

  ngOnDestroy(): void {
    this.dataSub?.unsubscribe();
    this.reflowSub?.unsubscribe();
    this.wrapper.detach();
  }

  @HostBinding('style.--axis-drivers-font-scale')
  get hostFontScale(): string {
    return this.fontScale.toString();
  }

  get eyebrow(): string {
    return this.data?.eyebrow || this.data?.heading || '';
  }

  get sectionTitle(): string {
    return this.data?.subheading || this.data?.title || '';
  }

  trackByRow(index: number, row: AxisDriverRow): string {
    return `${row.label}-${row.status}-${index}`;
  }

  getStatusStyle(status: string): Record<string, string> {
    const key = this.normalizeStatus(status);
    const configured = this.data?.statusStyles?.[status] || this.data?.statusStyles?.[key];
    const fallback = this.defaultStatusStyles[key] || {
      background: '#eef2f7',
      color: '#334155',
    };

    return {
      background: configured?.background || fallback.background || '#eef2f7',
      color: configured?.color || fallback.color || '#334155',
    };
  }

  private loadRows(filters: any = {}): void {
    const filterLoadSignature = this.getFilterLoadSignature(filters);
    if (filters?.widgetId && filterLoadSignature === this.lastFilterLoadSignature) {
      return;
    }
    this.lastFilterLoadSignature = filterLoadSignature;

    this.dataSub?.unsubscribe();
    this.errorMessage = '';

    if (!this.data?.apiUrl) {
      this.rows = this.extractRows(this.data);
      return;
    }

    this.loading = true;
    this.dataSub = this.configService.getConfig(this.data.apiUrl, this.data, filters, this.data.method).subscribe({
      next: (response) => {
        this.rows = this.extractRows(response);
        this.loading = false;
      },
      error: () => {
        this.rows = [];
        this.loading = false;
        this.errorMessage = 'Unable to load axis driver data.';
      },
    });
  }

  private applyResponsiveSizing(): void {
    this.fontScale = this.responsiveLayoutService.getFontScale();
  }

  private extractRows(source: unknown): AxisDriverRow[] {
    const candidates = this.getRowCandidates(source);

    for (const candidate of candidates) {
      const rows = this.toRows(candidate);
      if (rows.length) {
        return rows;
      }
    }

    return [];
  }

  private getRowCandidates(source: unknown): unknown[] {
    const sourceRecord = this.asRecord(source);
    const dataKey = this.data?.dataKey;
    const configuredPath = this.data?.dataPath || this.data?.responsePath;
    const candidates: unknown[] = [];

    if (configuredPath) {
      candidates.push(this.resolvePath(source, configuredPath));
      candidates.push(this.resolvePath(source, `responseList.0.data.0.${configuredPath}`));
      candidates.push(this.resolvePath(source, `data.0.${configuredPath}`));
    }

    if (dataKey) {
      candidates.push(sourceRecord?.[dataKey]);
      candidates.push(this.resolvePath(source, `responseList.0.data.0.${dataKey}`));
      candidates.push(this.resolvePath(source, `data.0.${dataKey}`));
    }

    candidates.push(
      sourceRecord?.['rows'],
      sourceRecord?.['items'],
      sourceRecord?.['data'],
      this.resolvePath(source, 'responseList.0.data.0.rows'),
      this.resolvePath(source, 'responseList.0.data.0.items'),
      this.resolvePath(source, 'responseList.0.data.0'),
      this.resolvePath(source, 'responseList.0.data'),
      source,
    );

    return candidates;
  }

  private toRows(candidate: unknown): AxisDriverRow[] {
    const candidateRecord = this.asRecord(candidate);
    if (candidateRecord) {
      const nestedCandidates = [
        candidateRecord['rows'],
        candidateRecord['items'],
        candidateRecord['data'],
      ];

      for (const nestedCandidate of nestedCandidates) {
        const rows = this.toRows(nestedCandidate);
        if (rows.length) {
          return rows;
        }
      }
    }

    if (!Array.isArray(candidate)) {
      return [];
    }

    const labelKey = this.data?.labelKey || 'label';
    const statusKey = this.data?.statusKey || 'status';

    return candidate
      .map((item) => {
        const record = this.asRecord(item);
        const label = record ? record[labelKey] ?? record['name'] ?? record['factor'] : '';
        const status = record ? record[statusKey] ?? record['value'] ?? record['rating'] : '';

        return {
          label: String(label ?? '').trim(),
          status: String(status ?? '').trim(),
        };
      })
      .filter((row) => row.label && row.status);
  }

  private resolvePath(source: unknown, path: string): unknown {
    if (!path) {
      return undefined;
    }

    return path.split('.').reduce<unknown>((current, segment) => {
      if (current === null || current === undefined) {
        return undefined;
      }

      if (Array.isArray(current)) {
        const index = Number(segment);
        return Number.isInteger(index) ? current[index] : undefined;
      }

      if (typeof current === 'object') {
        return (current as Record<string, unknown>)[segment];
      }

      return undefined;
    }, source);
  }

  private asRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : null;
  }

  private normalizeStatus(status: string): string {
    return String(status || '').trim().toLowerCase();
  }

  private getFilterLoadSignature(filters: any): string {
    return JSON.stringify({
      requestParams: filters?.requestParams || {},
      queryParams: filters?.queryParams || {},
      widgetId: filters?.widgetId || null,
      changedFilterKey: filters?.changedFilterKey || null,
      filterListener: filters?.filterListener || filters?.filterListner || null,
    });
  }
}
